/**
 * The "realistic" cow: a tumbling rigid ellipsoid.
 *
 * Still a simplification (it is an ellipsoid, not a cow), but it has the
 * two things the sphere lacks: an orientation-dependent cross-section, and
 * torque, so it tumbles.
 *
 * Body frame: x along the length (head toward +x), y up through the back,
 * z across the width. Semi-axes ax >= ay >= az.
 *
 * Drag: F = 0.5 rho C_d A(n) |v_rel| v_rel, where n is the unit relative
 * wind in the body frame and A(n) is the ellipsoid's projected area
 * perpendicular to n:
 *   A(n) = pi * sqrt( ay^2 az^2 nx^2 + ax^2 az^2 ny^2 + ax^2 ay^2 nz^2 )
 * (exact; reduces to pi r^2 for a sphere and pi ay az end-on).
 *
 * Torque: drag acts at a center of pressure offset cpOffset along +x from
 * the center of mass (assumption), plus quadratic aerodynamic damping
 * -C_rot rho L^5 |w| w that keeps spin bounded (assumption).
 *
 * Rotation: Euler's equations in the body frame with the solid-ellipsoid
 * inertia tensor I = m/5 diag(ay^2+az^2, ax^2+az^2, ax^2+ay^2), and the
 * quaternion kinematics q' = 0.5 q (0, w). Integrated with explicit
 * midpoint RK2 in substeps of at most MAX_SUBSTEP_S.
 *
 * Ground: the lowest point of a rotated ellipsoid above its center is
 * h = sqrt(ax^2 ux^2 + ay^2 uy^2 + az^2 uz^2), u = world up in the body
 * frame. On contact the cow stops: kinetic friction (mu m g ~ 3,460 N at
 * mu = 0.5) exceeds the drag at storm wind speeds (~600 N), so it does not
 * slide, and it does not roll like the sphere.
 *
 * Settling: an ellipsoid resting on a plane is stable only with its
 * smallest semi-axis vertical (the minimum of center height, so of
 * potential energy). Resting on the middle axis is a saddle and on the
 * longest axis a maximum. On landing the body snaps to the stable pose,
 * lying on its side (body z vertical), keeping its compass heading. The
 * toppling motion itself is not simulated.
 */
import { STANDARD_GRAVITY_MPS2 } from '../config/constants';
import type { WindField, WindSample } from './windField';

export interface EllipsoidCowParams {
  massKg: number;
  /** Semi-axes, m: length, height, width. */
  ax: number;
  ay: number;
  az: number;
  airDensity: number;
  dragCoefficient: number;
  /** Center of pressure ahead of the center of mass along body +x, m. */
  cpOffsetM: number;
  /** Dimensionless rotational damping coefficient. */
  rotDamping: number;
}

export interface EllipsoidCowState {
  posX: number;
  posY: number;
  posZ: number;
  velX: number;
  velY: number;
  velZ: number;
  prevX: number;
  prevY: number;
  prevZ: number;
  /** Orientation quaternion (body to world). */
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  /** Angular velocity in the body frame, rad/s. */
  wx: number;
  wy: number;
  wz: number;
  onGround: boolean;
}

/**
 * Default shape: same mass and water density as the spherical cow (volume
 * 0.705 m^3), semi-axis ratio 1.2 : 0.75 : 0.4. The ratio is an assumption
 * chosen to look cow-proportioned, not a measurement.
 */
export function defaultEllipsoidCow(
  massKg: number,
  densityKgM3: number,
  airDensity: number,
): EllipsoidCowParams {
  const ratio = [1.2, 0.75, 0.4] as const;
  const volume = massKg / densityKgM3;
  const k = Math.cbrt(volume / ((4 / 3) * Math.PI * ratio[0] * ratio[1] * ratio[2]));
  return {
    massKg,
    ax: ratio[0] * k,
    ay: ratio[1] * k,
    az: ratio[2] * k,
    airDensity,
    dragCoefficient: 0.47,
    cpOffsetM: 0.15 * ratio[0] * k,
    rotDamping: 0.5,
  };
}

export function inertia(p: EllipsoidCowParams): [number, number, number] {
  const m5 = p.massKg / 5;
  return [
    m5 * (p.ay * p.ay + p.az * p.az),
    m5 * (p.ax * p.ax + p.az * p.az),
    m5 * (p.ax * p.ax + p.ay * p.ay),
  ];
}

/** Projected area perpendicular to unit direction (nx, ny, nz) in the body frame. */
export function projectedArea(p: EllipsoidCowParams, nx: number, ny: number, nz: number): number {
  const { ax, ay, az } = p;
  return (
    Math.PI *
    Math.sqrt(
      ay * ay * az * az * nx * nx + ax * ax * az * az * ny * ny + ax * ax * ay * ay * nz * nz,
    )
  );
}

/** Height of the center above the lowest point, given world up (ux, uy, uz) in the body frame. */
export function supportHeight(p: EllipsoidCowParams, ux: number, uy: number, uz: number): number {
  return Math.sqrt(p.ax * p.ax * ux * ux + p.ay * p.ay * uy * uy + p.az * p.az * uz * uz);
}

export function createEllipsoidCowState(): EllipsoidCowState {
  return {
    posX: 0,
    posY: 0,
    posZ: 0,
    velX: 0,
    velY: 0,
    velZ: 0,
    prevX: 0,
    prevY: 0,
    prevZ: 0,
    qx: 0,
    qy: 0,
    qz: 0,
    qw: 1,
    wx: 0,
    wy: 0,
    wz: 0,
    onGround: false,
  };
}

const MAX_SUBSTEP_S = 0.05;

// Scratch (single body, single thread).
const wind: WindSample = { x: 0, y: 0, z: 0 };
const v3 = { x: 0, y: 0, z: 0 };

/** Rotate world vector (x, y, z) into the body frame by conjugate(q). Writes v3. */
function toBody(
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  x: number,
  y: number,
  z: number,
): void {
  // v' = q* v q
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  // With t = 2 (q_v x v): q* v q = v - q_w t + q_v x t.
  v3.x = x - qw * tx + (qy * tz - qz * ty);
  v3.y = y - qw * ty + (qz * tx - qx * tz);
  v3.z = z - qw * tz + (qx * ty - qy * tx);
}

/** Rotate body vector (x, y, z) into the world frame by q. Writes v3. */
function toWorld(
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  x: number,
  y: number,
  z: number,
): void {
  // With t = 2 (q_v x v): q v q* = v + q_w t + q_v x t.
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  v3.x = x + qw * tx + (qy * tz - qz * ty);
  v3.y = y + qw * ty + (qz * tx - qx * tz);
  v3.z = z + qw * tz + (qx * ty - qy * tx);
}

interface Deriv {
  ax: number;
  ay: number;
  az: number;
  // body angular acceleration
  alx: number;
  aly: number;
  alz: number;
}
const d1: Deriv = { ax: 0, ay: 0, az: 0, alx: 0, aly: 0, alz: 0 };
const d2: Deriv = { ax: 0, ay: 0, az: 0, alx: 0, aly: 0, alz: 0 };

function derivatives(
  p: EllipsoidCowParams,
  I: readonly [number, number, number],
  field: WindField | null,
  tS: number,
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  wx: number,
  wy: number,
  wz: number,
  gravity: boolean,
  out: Deriv,
): void {
  if (field) field.sample(px, py, pz, tS, wind);
  else {
    wind.x = 0;
    wind.y = 0;
    wind.z = 0;
  }
  const rx = wind.x - vx;
  const ry = wind.y - vy;
  const rz = wind.z - vz;
  const speed = Math.sqrt(rx * rx + ry * ry + rz * rz);
  let fx = 0;
  let fy = 0;
  let fz = 0;
  // Torque in body frame.
  let tx = 0;
  let ty = 0;
  let tz = 0;
  if (speed > 1e-9 && p.airDensity > 0) {
    toBody(qx, qy, qz, qw, rx / speed, ry / speed, rz / speed);
    const nbx = v3.x;
    const nby = v3.y;
    const nbz = v3.z;
    const area = projectedArea(p, nbx, nby, nbz);
    const k = 0.5 * p.airDensity * p.dragCoefficient * area * speed;
    fx = k * rx;
    fy = k * ry;
    fz = k * rz;
    // Force in body frame = k * speed * n_body; torque = r_cp x F, r_cp = (cp, 0, 0).
    const fbm = k * speed;
    const fby = fbm * nby;
    const fbz = fbm * nbz;
    ty = -p.cpOffsetM * fbz;
    tz = p.cpOffsetM * fby;
  }
  // Quadratic rotational damping.
  const wmag = Math.sqrt(wx * wx + wy * wy + wz * wz);
  const L = p.ax;
  const cDamp = p.rotDamping * p.airDensity * L * L * L * L * L * wmag;
  tx -= cDamp * wx;
  ty -= cDamp * wy;
  tz -= cDamp * wz;

  out.ax = fx / p.massKg;
  out.ay = fy / p.massKg - (gravity ? STANDARD_GRAVITY_MPS2 : 0);
  out.az = fz / p.massKg;
  // Euler's equations: I w' = tau - w x (I w)
  const [Ix, Iy, Iz] = I;
  out.alx = (tx - (Iz - Iy) * wy * wz) / Ix;
  out.aly = (ty - (Ix - Iz) * wz * wx) / Iy;
  out.alz = (tz - (Iy - Ix) * wx * wy) / Iz;
}

/** q += 0.5 * h * q (0, w), then normalize. Writes into state. */
function integrateQuat(s: EllipsoidCowState, wx: number, wy: number, wz: number, h: number): void {
  const { qx, qy, qz, qw } = s;
  const hx = 0.5 * h;
  const nx = qx + hx * (qw * wx + qy * wz - qz * wy);
  const ny = qy + hx * (qw * wy + qz * wx - qx * wz);
  const nz = qz + hx * (qw * wz + qx * wy - qy * wx);
  const nw = qw + hx * (-qx * wx - qy * wy - qz * wz);
  const inv = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz + nw * nw);
  s.qx = nx * inv;
  s.qy = ny * inv;
  s.qz = nz * inv;
  s.qw = nw * inv;
}

/**
 * Advance by dtS. field may be null (still air, used by tests). gravity
 * false gives torque-free tests a clean setup. Allocation-free.
 */
export function stepEllipsoidCow(
  p: EllipsoidCowParams,
  s: EllipsoidCowState,
  field: WindField | null,
  tS: number,
  dtS: number,
  gravity = true,
  ground = true,
): void {
  s.prevX = s.posX;
  s.prevY = s.posY;
  s.prevZ = s.posZ;
  const I = inertia(p);
  const n = Math.max(1, Math.ceil(dtS / MAX_SUBSTEP_S));
  const h = dtS / n;
  const half = 0.5 * h;
  let t = tS;
  for (let i = 0; i < n; i++) {
    if (s.onGround) {
      // Resting: friction beats drag (see header). Nothing moves.
      s.velX = s.velY = s.velZ = 0;
      s.wx = s.wy = s.wz = 0;
      t += h;
      continue;
    }
    derivatives(
      p,
      I,
      field,
      t,
      s.posX,
      s.posY,
      s.posZ,
      s.velX,
      s.velY,
      s.velZ,
      s.qx,
      s.qy,
      s.qz,
      s.qw,
      s.wx,
      s.wy,
      s.wz,
      gravity,
      d1,
    );
    // Midpoint state
    const mvx = s.velX + half * d1.ax;
    const mvy = s.velY + half * d1.ay;
    const mvz = s.velZ + half * d1.az;
    const mwx = s.wx + half * d1.alx;
    const mwy = s.wy + half * d1.aly;
    const mwz = s.wz + half * d1.alz;
    // Midpoint orientation (half step of rotation)
    const q0x = s.qx;
    const q0y = s.qy;
    const q0z = s.qz;
    const q0w = s.qw;
    integrateQuat(s, s.wx, s.wy, s.wz, half);
    const mqx = s.qx;
    const mqy = s.qy;
    const mqz = s.qz;
    const mqw = s.qw;
    s.qx = q0x;
    s.qy = q0y;
    s.qz = q0z;
    s.qw = q0w;
    derivatives(
      p,
      I,
      field,
      t + half,
      s.posX + half * s.velX,
      s.posY + half * s.velY,
      s.posZ + half * s.velZ,
      mvx,
      mvy,
      mvz,
      mqx,
      mqy,
      mqz,
      mqw,
      mwx,
      mwy,
      mwz,
      gravity,
      d2,
    );
    s.posX += h * mvx;
    s.posY += h * mvy;
    s.posZ += h * mvz;
    s.velX += h * d2.ax;
    s.velY += h * d2.ay;
    s.velZ += h * d2.az;
    integrateQuat(s, mwx, mwy, mwz, h);
    s.wx += h * d2.alx;
    s.wy += h * d2.aly;
    s.wz += h * d2.alz;

    if (ground) {
      toBody(s.qx, s.qy, s.qz, s.qw, 0, 1, 0);
      const hSupport = supportHeight(p, v3.x, v3.y, v3.z);
      if (s.posY <= hSupport) {
        settle(s);
        s.posY = p.az;
        s.onGround = true;
        s.velX = s.velY = s.velZ = 0;
        s.wx = s.wy = s.wz = 0;
      }
    }
    t += h;
  }
}

/** Height of the center above the lowest point, for the current orientation. Allocation-free. */
export function currentSupportHeight(p: EllipsoidCowParams, s: EllipsoidCowState): number {
  toBody(s.qx, s.qy, s.qz, s.qw, 0, 1, 0);
  return supportHeight(p, v3.x, v3.y, v3.z);
}

/**
 * Rotate to the stable resting pose: body z along world +-y (whichever is
 * closer), body x along the horizontal projection of its current heading.
 * Allocation-free.
 */
function settle(s: EllipsoidCowState): void {
  // Current body x and z axes in world.
  toWorld(s.qx, s.qy, s.qz, s.qw, 1, 0, 0);
  let hx = v3.x;
  let hz = v3.z;
  toWorld(s.qx, s.qy, s.qz, s.qw, 0, 0, 1);
  const sgn = v3.y >= 0 ? 1 : -1;
  let hn = Math.sqrt(hx * hx + hz * hz);
  if (hn < 1e-6) {
    // Body x pointed straight up or down: any heading will do.
    hx = 1;
    hz = 0;
    hn = 1;
  }
  hx /= hn;
  hz /= hn;
  // Body axes in world: X = (hx, 0, hz), Z = (0, sgn, 0), Y = Z x X.
  const Xx = hx;
  const Xy = 0;
  const Xz = hz;
  const Zx = 0;
  const Zy = sgn;
  const Zz = 0;
  const Yx = Zy * Xz - Zz * Xy;
  const Yy = Zz * Xx - Zx * Xz;
  const Yz = Zx * Xy - Zy * Xx;
  // Rotation matrix (columns X, Y, Z) to quaternion (Shepperd's method).
  const m00 = Xx;
  const m11 = Yy;
  const m22 = Zz;
  const tr = m00 + m11 + m22;
  if (tr > 0) {
    const k = 0.5 / Math.sqrt(tr + 1);
    s.qw = 0.25 / k;
    s.qx = (Yz - Zy) * k;
    s.qy = (Zx - Xz) * k;
    s.qz = (Xy - Yx) * k;
  } else if (m00 > m11 && m00 > m22) {
    const k = 2 * Math.sqrt(1 + m00 - m11 - m22);
    s.qw = (Yz - Zy) / k;
    s.qx = 0.25 * k;
    s.qy = (Yx + Xy) / k;
    s.qz = (Zx + Xz) / k;
  } else if (m11 > m22) {
    const k = 2 * Math.sqrt(1 + m11 - m00 - m22);
    s.qw = (Zx - Xz) / k;
    s.qx = (Yx + Xy) / k;
    s.qy = 0.25 * k;
    s.qz = (Zy + Yz) / k;
  } else {
    const k = 2 * Math.sqrt(1 + m22 - m00 - m11);
    s.qw = (Xy - Yx) / k;
    s.qx = (Zx + Xz) / k;
    s.qy = (Zy + Yz) / k;
    s.qz = 0.25 * k;
  }
}

/** Place at rest with a given orientation and spin (body frame). */
export function resetEllipsoidCow(
  s: EllipsoidCowState,
  x: number,
  y: number,
  z: number,
  q: readonly [number, number, number, number],
  w: readonly [number, number, number],
): void {
  s.posX = s.prevX = x;
  s.posY = s.prevY = y;
  s.posZ = s.prevZ = z;
  s.velX = s.velY = s.velZ = 0;
  [s.qx, s.qy, s.qz, s.qw] = q;
  [s.wx, s.wy, s.wz] = w;
  s.onGround = false;
}

/** Exposed for tests: rotate a world vector into the body frame. */
export function worldToBody(
  s: EllipsoidCowState,
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  toBody(s.qx, s.qy, s.qz, s.qw, x, y, z);
  return [v3.x, v3.y, v3.z];
}

/** Exposed for tests: rotate a body vector into the world frame. */
export function bodyToWorld(
  s: EllipsoidCowState,
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  toWorld(s.qx, s.qy, s.qz, s.qw, x, y, z);
  return [v3.x, v3.y, v3.z];
}
